import { reactive } from 'vue';
import * as OM from '@/model';
import * as VM from '@/viewmodel';
import { StorageServices } from './StorageServices';
import { FileDownloaderServices } from './FileDownloaderServices';
import store from '@/store';

class _DownloadedFilesServices {

    // chiave: qrConfig.identifier (== id passato a FileDownloaderServices.enqueue)
    progress: Record<string, number> = reactive({});

    // qrConfig.identifier di tutti i file già scaricati per offline (per badge "già scaricato" nelle liste)
    downloadedIdentifiers: Set<string> = reactive(new Set(this.getAll().map(x => x.qrConfigIdentifier)));

    private get userIdentifier(): string {
        return store.state.user?.identifier;
    }

    getAll(): VM.DownloadedQrFileVm[] {
        if (!this.userIdentifier)
            return [];

        return StorageServices.getDownloadedFiles(this.userIdentifier);
    }

    // la lista è scoped per utente: richiamare al login/logout per ricaricare lo stato in memoria sul nuovo utente attivo
    reload() {
        Object.keys(this.progress).forEach(k => delete this.progress[k]);
        this.downloadedIdentifiers.clear();
        this.getAll().forEach(x => this.downloadedIdentifiers.add(x.qrConfigIdentifier));
    }

    download(qrConfig: OM.QrConfig): Promise<VM.DownloadedQrFileVm> {
        let commessa = store.state.commesse.selectedCommessa;

        return FileDownloaderServices.getDownloadDirectory()
        .then(baseDir => {
            let ext = this.extractExtension(qrConfig.redirectToUrl);
            let fileName = this.sanitizeFileName(qrConfig.name || qrConfig.identifier) + (ext ? '.' + ext : '');
            // cartelle per utente e per commessa: evitano collisioni tra utenti diversi e tra commesse diverse dello stesso utente
            let destPath = baseDir + '/qrDownloads/' + this.userIdentifier + '/' + commessa.identifier + '/' + fileName;

            this.progress[qrConfig.identifier] = 0;

            return FileDownloaderServices.enqueue(qrConfig.redirectToUrl, { destPath, id: qrConfig.identifier }, event => {
                this.progress[qrConfig.identifier] = event.percent;
            })
            .then(result => {
                delete this.progress[qrConfig.identifier];

                let vm = new VM.DownloadedQrFileVm();
                vm.qrConfigIdentifier = qrConfig.identifier;
                vm.qrConfigName = qrConfig.name;
                vm.commessaName = commessa.name;
                vm.url = qrConfig.redirectToUrl;
                vm.fileName = fileName;
                vm.localPath = result.path;
                vm.downloadedOn = new Date();

                let all = this.getAll().filter(x => x.qrConfigIdentifier != qrConfig.identifier);
                all.unshift(vm);
                StorageServices.setDownloadedFiles(this.userIdentifier, all);
                this.downloadedIdentifiers.add(qrConfig.identifier);

                return vm;
            })
            .catch(err => {
                delete this.progress[qrConfig.identifier];
                throw err;
            });
        });
    }

    // riallinea lo stato di progresso dopo un reload del webview/avvio app, leggendo
    // la coda nativa (i task in corso sopravvivono come foreground service Android/background iOS)
    rehydrateProgress(): Promise<void> {
        if (!FileDownloaderServices.isAvailable)
            return Promise.resolve();

        return FileDownloaderServices.getQueue()
        .then(queue => {
            queue.forEach(item => {
                if (item.status == 'downloading' || item.status == 'queued')
                    this.progress[item.id] = item.percent;
            });
        });
    }

    open(file: VM.DownloadedQrFileVm): Promise<void> {
        return FileDownloaderServices.openFile(file.localPath);
    }

    remove(qrConfigIdentifier: string) {
        let all = this.getAll().filter(x => x.qrConfigIdentifier != qrConfigIdentifier);
        StorageServices.setDownloadedFiles(this.userIdentifier, all);
        this.downloadedIdentifiers.delete(qrConfigIdentifier);
    }

    private extractExtension(url: string): string {
        try {
            let pathname = new URL(url).pathname;
            let match = pathname.match(/\.([a-zA-Z0-9]{1,8})$/);
            return match ? match[1] : '';
        } catch {
            return '';
        }
    }

    private sanitizeFileName(name: string): string {
        return (name || 'file').replace(/[^a-zA-Z0-9_\-]/g, '_');
    }
}

export let DownloadedFilesServices = new _DownloadedFilesServices();
